import { useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "./api";
import { useAuth } from "./auth";

/**
 * Subscribe to live case events from the backend WebSocket.
 *
 *   const { connected, lastEvent } = useCaseEvents((evt) => { ... });
 *
 * Auto-reconnects with exponential backoff. Only Admin / Backoffice
 * connections succeed (server-side check).
 */
export default function useCaseEvents(onEvent) {
  const { accessToken, user } = useAuth();
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const handlerRef = useRef(onEvent);

  // Keep latest handler ref without re-opening the socket on every render.
  useEffect(() => { handlerRef.current = onEvent; }, [onEvent]);

  useEffect(() => {
    if (!accessToken || !user) return;
    if (!["admin", "backoffice"].includes(user.role)) return;

    let stopped = false;
    let timer = null;

    const connect = () => {
      if (stopped) return;
      const proto = BACKEND_URL.startsWith("https") ? "wss" : "ws";
      const host = BACKEND_URL.replace(/^https?:\/\//, "");
      const url = `${proto}://${host}/api/ws/cases?token=${encodeURIComponent(accessToken)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        retryRef.current = 0;
        setConnected(true);
      };
      ws.onmessage = (msg) => {
        try {
          const evt = JSON.parse(msg.data);
          setLastEvent(evt);
          handlerRef.current?.(evt);
        } catch (_) { /* ignore */ }
      };
      ws.onclose = () => {
        setConnected(false);
        if (stopped) return;
        const delay = Math.min(30000, 1000 * 2 ** Math.min(retryRef.current, 5));
        retryRef.current += 1;
        timer = setTimeout(connect, delay);
      };
      ws.onerror = () => { ws.close(); };
    };

    connect();
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      try { wsRef.current?.close(); } catch (_) { /* ignore */ }
    };
  }, [accessToken, user]);

  return { connected, lastEvent };
}
